(function() {
    'use strict';

    /**
     * Storefront-only chat core for Shopify embed.
     * Collections: chatSessions, chatMessages. Exposes window.DashboardChatCore
     * (createSession, sendUserMessage, onMessagesForSession, getMessagesForSession).
     * Staff chat lives in dashboard-react. This file is only the storefront copy.
     */

    var messagesBySession = {};
    var messageListenersBySession = {};
    var firestoreUnsubBySession = {};

    function getDb() {
        try {
            if (window.db) return window.db;
            if (window.firebase && window.firebase.firestore) return window.firebase.firestore();
        } catch (e) {}
        return null;
    }

    function getServerTimestamp() {
        try {
            if (window.firebase && window.firebase.firestore && window.firebase.firestore.FieldValue && window.firebase.firestore.FieldValue.serverTimestamp)
                return window.firebase.firestore.FieldValue.serverTimestamp();
        } catch (e) {}
        return new Date();
    }

    function now() { return new Date(); }

    function materializeTimestamp(value) {
        if (!value) return now();
        if (value.toDate) return value.toDate();
        if (value.toMillis) return new Date(value.toMillis());
        if (value instanceof Date) return value;
        return now();
    }

    function notifyMessages(sessionId) {
        var list = (messagesBySession[sessionId] || []).slice();
        var listeners = messageListenersBySession[sessionId];
        if (listeners) listeners.forEach(function(fn) {
            try { fn(list); } catch (e) {}
        });
    }

    function ensureMessageListener(sessionId) {
        var db = getDb();
        if (!db || !sessionId) return;
        if (firestoreUnsubBySession[sessionId]) return;
        messageListenersBySession[sessionId] = messageListenersBySession[sessionId] || [];
        messagesBySession[sessionId] = messagesBySession[sessionId] || [];
        try {
            var query = db.collection('chatMessages')
                .where('sessionId', '==', sessionId)
                .limit(200);
            function buildList(snap) {
                var list = [];
                snap.forEach(function(doc) {
                    var d = doc.data() || {};
                    list.push({
                        id: doc.id,
                        role: d.role || 'system',
                        text: d.text || '',
                        createdAt: materializeTimestamp(d.createdAt),
                        meta: { origin: d.origin, name: d.name, email: d.email }
                    });
                });
                list.sort(function(a, b) {
                    var ta = a.createdAt && a.createdAt.getTime ? a.createdAt.getTime() : 0;
                    var tb = b.createdAt && b.createdAt.getTime ? b.createdAt.getTime() : 0;
                    return ta - tb;
                });
                return list;
            }
            var unsub = query.onSnapshot(
                function(snap) {
                    var list = buildList(snap);
                    messagesBySession[sessionId] = list;
                    notifyMessages(sessionId);
                },
                function(err) {
                    if (typeof console !== 'undefined' && console.warn) console.warn('Chat messages listener:', err && (err.message || err.code));
                    query.get({ source: 'server' }).then(function(snap) {
                        var list = buildList(snap);
                        messagesBySession[sessionId] = list;
                        notifyMessages(sessionId);
                    }).catch(function() {});
                }
            );
            firestoreUnsubBySession[sessionId] = unsub;
            query.get({ source: 'server' }).then(function(snap) {
                var list = buildList(snap);
                messagesBySession[sessionId] = list;
                notifyMessages(sessionId);
            }).catch(function() {});
        } catch (e) {
            if (typeof console !== 'undefined' && console.warn) console.warn('ensureMessageListener:', e);
        }
    }

    function removeMessageListener(sessionId, listener) {
        var list = messageListenersBySession[sessionId];
        if (!list) return;
        var i = list.indexOf(listener);
        if (i !== -1) list.splice(i, 1);
        if (list.length === 0 && firestoreUnsubBySession[sessionId]) {
            try { firestoreUnsubBySession[sessionId](); } catch (e) {}
            delete firestoreUnsubBySession[sessionId];
        }
    }

    function contextPayload(context) {
        if (!context) return null;
        var payload = {
            pageType: context.pageType || '',
            url: context.url || '',
            title: context.title || '',
            productId: context.productId || '',
            productHandle: context.productHandle || '',
            productTitle: context.productTitle || '',
            variantId: context.variantId || '',
            variantTitle: context.variantTitle || '',
            options: context.options || '',
            capturedAt: getServerTimestamp()
        };
        var hasText = payload.title || payload.url || payload.productTitle;
        return hasText ? payload : null;
    }

    function createSession(opts) {
        opts = opts || {};
        var customerName = (opts.customerName || opts.name || '').toString().trim();
        var customerEmail = (opts.customerEmail || opts.email || '').toString().trim();
        var requestedUid = opts.customerUid ? String(opts.customerUid).trim() : '';
        var authUid = (window.auth && window.auth.currentUser && window.auth.currentUser.uid) ? String(window.auth.currentUser.uid) : '';
        if (!authUid) return Promise.reject(new Error('Not signed in'));
        if (requestedUid && requestedUid !== authUid) return Promise.reject(new Error('Conversation owner mismatch'));
        var db = getDb();
        if (!db) return Promise.reject(new Error('Firestore not initialized (db missing). Check script order and CSP.'));
        var ref = db.collection('chatSessions').doc();
        var nowTs = getServerTimestamp();
        var data = {
            createdAt: nowTs,
            updatedAt: nowTs,
            lastMessageAt: nowTs,
            lastMessageText: '',
            customerName: customerName,
            customerEmail: customerEmail,
            status: 'open',
            unreadCount: 0,
            customerUid: authUid
        };
        var original = contextPayload(opts.originalContext);
        var current = contextPayload(opts.originalContext);
        if (original && current) {
            data.originalContext = original;
            data.currentContext = current;
            data.contextUpdatedAt = nowTs;
        }
        return ref.set(data).then(function() { return ref.id; }).catch(function(err) {
            if (!data.originalContext) return Promise.reject(err || new Error('createSession failed'));
            delete data.originalContext;
            delete data.currentContext;
            delete data.contextUpdatedAt;
            return ref.set(data).then(function() { return ref.id; }).catch(function(retryErr) {
                return Promise.reject(retryErr || err || new Error('createSession failed'));
            });
        });
    }

    function readOwnedSession(sessionId, uid) {
        var db = getDb();
        if (!db || !sessionId || !uid) return Promise.resolve(null);
        return db.collection('chatSessions').doc(sessionId).get().then(function (snap) {
            if (!snap.exists) return null;
            var data = snap.data() || {};
            if (String(data.customerUid || '') !== String(uid)) return null;
            return { id: snap.id, customerUid: data.customerUid };
        }).catch(function () { return null; });
    }

    function updateCurrentContext(sessionId, context) {
        var db = getDb();
        var payload = contextPayload(context);
        if (!db || !sessionId || !payload) return Promise.resolve();
        return db.collection('chatSessions').doc(sessionId).update({
            currentContext: payload,
            contextUpdatedAt: getServerTimestamp(),
            updatedAt: getServerTimestamp()
        }).catch(function() {});
    }

    function upsertPresence(presence) {
        var db = getDb();
        if (!db || !presence || !presence.sessionId || !presence.tabId || !presence.customerUid) return Promise.resolve();
        var ref = db.collection('chatPresence').doc(presence.sessionId + '_' + presence.tabId);
        // The first heartbeat must create the document. update() on a missing
        // document is permission-denied, so a create fallback never ran.
        var data = {
            sessionId: presence.sessionId,
            tabId: presence.tabId,
            customerUid: presence.customerUid,
            connectionState: presence.connectionState === 'away' ? 'away' : 'online',
            widgetState: presence.widgetState === 'open' ? 'open' : 'minimized',
            lastHeartbeatAt: getServerTimestamp(),
            lastSeenAt: getServerTimestamp()
        };
        if (presence.active) data.lastActivityAt = getServerTimestamp();
        return ref.set(data, { merge: true }).catch(function (err) {
            var code = err && err.code ? err.code : 'unknown';
            if (code === 'permission-denied') console.error('Chat presence write denied');
            else console.error('Chat presence write failed: ' + code);
            return Promise.reject(err);
        });
    }

    function ackStaffMessage(messageId, sessionId, customerUid, mode) {
        var db = getDb();
        if (!db || !messageId || !sessionId || !customerUid) return Promise.resolve();
        var data = { sessionId: sessionId, customerUid: customerUid };
        if (mode === 'seen') data.seenAt = getServerTimestamp();
        else data.deliveredAt = getServerTimestamp();
        return db.collection('chatReceipts').doc(messageId).set(data, { merge: true }).catch(function() {});
    }

    function updateSessionLastMessage(sessionId, text) {
        var db = getDb();
        if (!db || !sessionId) return Promise.resolve();
        return db.collection('chatSessions').doc(sessionId).update({
            lastMessageAt: getServerTimestamp(),
            lastMessageText: (text || '').toString().trim().substring(0, 200),
            updatedAt: getServerTimestamp()
        }).catch(function() {});
    }

    function sendUserMessage(text, meta) {
        var t = (text == null ? '' : String(text)).trim();
        if (!t) return null;
        meta = meta || {};
        var sessionId = meta.sessionId;
        var origin = meta.origin || 'dashboard';
        var source = meta.source || 'dashboard';
        var role = (origin === 'dashboard' || source === 'dashboard') ? 'assistant' : 'user';
        var dbSend = getDb();
        if (!dbSend || !sessionId) return null;
        var payload = {
            sessionId: sessionId,
            role: role,
            text: t,
            createdAt: getServerTimestamp(),
            origin: origin,
            source: source,
            name: meta.name || '',
            email: meta.email || ''
        };
        dbSend.collection('chatMessages').add(payload).then(function() {
            updateSessionLastMessage(sessionId, t);
        }).catch(function() {});
        return { id: 'pending', role: role, text: t, createdAt: now(), meta: meta };
    }

    function getMessagesForSession(sessionId) {
        ensureMessageListener(sessionId);
        return (messagesBySession[sessionId] || []).slice();
    }

    function onMessagesForSession(sessionId, listener) {
        if (!sessionId || typeof listener !== 'function') return function() {};
        ensureMessageListener(sessionId);
        if (!messageListenersBySession[sessionId]) messageListenersBySession[sessionId] = [];
        messageListenersBySession[sessionId].push(listener);
        try { listener((messagesBySession[sessionId] || []).slice()); } catch (e) {}
        return function() {
            removeMessageListener(sessionId, listener);
        };
    }

    window.DashboardChatCore = {
        createSession: createSession,
        readOwnedSession: readOwnedSession,
        updateCurrentContext: updateCurrentContext,
        upsertPresence: upsertPresence,
        ackStaffMessage: ackStaffMessage,
        sendUserMessage: sendUserMessage,
        onMessagesForSession: onMessagesForSession,
        getMessagesForSession: getMessagesForSession
    };
})();